/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ILabelProps, ILabelStyles, Label } from '@fluentui/react/lib/Label';
import { Stack } from '@fluentui/react/lib/Stack';
import { Toggle } from '@fluentui/react/lib/Toggle';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { CalloutButton } from 'src/core/components/CalloutButton';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { useFormState } from 'src/core/hooks/useFormState';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { AzureCsAccountStore, AzureSku } from 'src/core/stores/AzureCsAccountStore';
import { AzureResourceGroupStore } from 'src/core/stores/AzureResourceGroupStore';
import { AzureSubscriptionStore } from 'src/core/stores/AzureSubscriptionStore';
import { AzureTenantStore } from 'src/core/stores/AzureTenantStore';
import { AuthoringRegion, Region } from 'src/core/types/regionTypes';
import { getOrDefaultRegionFromResource } from 'src/core/utils/csAccountUtils';
import { FormValidators } from 'src/core/utils/formValidators';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { allowedCsAccountRegions, allPublicRegions, privateAuthoringRegions, publicAuthoringRegions } from 'src/core/utils/regionUtils';
import { azureResourceModalsMessages } from 'src/modules/azure/components/azureResourceModalsMessages';

export type AzureCsAccountCreateFormProps = {
    /**
     * The account type to create.
     */
    mode: AzureCsAccountStore['kind'];

    /**
     * A starter azure subscription to be used to create the new azure cs account.
     */
    subscriptionId?: AzureSubscriptionStore['id'];

    /**
     * A flag indicates whether to show the new azure cs account region dropdown.
     */
    showRegion?: boolean;

    /**
     * A flag indicates whether to show the new azure cs account managed identity toggle
     */
    showManagedIdentityToggle?: boolean;

    /**
     * Options controlling the pricing tier dropdown
     */
    showPricingTier?: boolean;

    /**
     * Predicate to filter pricing options.
     */
    pricingFilterPredicate?: (tier: AzureCsAccountStore['sku']['code']) => boolean;

    /**
     * A callback when the azure cs account managed identity toggle changes.
     */
    onAzureCsAccountDtoChange: (csAccountDto: AzureCsAccountCreateFormDto) => void;

    /**
     * Custom on submit callback for the Form.
     */
    onSubmit?: () => void;
};

export type AzureCsAccountCreateFormDto = {
    name: string;
    tenant: string;
    region: Region;
    isValid?: boolean;
    isManagedIdentityEnabled?: boolean;
    subscriptionId: AzureSubscriptionStore['id'];
    pricingTier: AzureCsAccountStore['sku']['code'];
    resourceGroupName: AzureResourceGroupStore['name'];
};

type RegionsState = { kind: 'noRegions' } | { kind: 'regions'; regions: Region[] };

const flexLabelStyles = classNamesFunction<ILabelProps, ILabelStyles>()({ root: { display: 'flex' } });

/**
 * Returns whether we should display regions picker or not,
 * and if yes what should be the display regions.
 */
const resolveRegionsState = (options: {
    isAuthoringCreationMode: boolean;
    currentRegion: AuthoringRegion;
    insideApplication: boolean;
}): RegionsState => {
    const { isAuthoringCreationMode, currentRegion, insideApplication } = options;
    const isPrivateCloud = privateAuthoringRegions.some(region => region === currentRegion);

    /**
     * The only use case we have for authoring resource creation
     * inside an app is when creating an authoring resource to move
     * the app to. However, you can not move an app across public
     * regions because that implies the app moving across region
     * databases.
     *
     * Private clouds don't allow other regions than their specific
     * region. Thus multi region dropdowns only work for public clouds.
     */
    if (insideApplication || isPrivateCloud) {
        if (isAuthoringCreationMode) {
            return { kind: 'noRegions' };
        }
        return { kind: 'regions', regions: allowedCsAccountRegions.get(currentRegion) };
    }
    if (isAuthoringCreationMode) {
        return { kind: 'regions', regions: publicAuthoringRegions };
    }
    return { kind: 'regions', regions: allPublicRegions };
};

const defaultPredicate = (sku: AzureCsAccountStore['sku']['code']) => sku === 'F0' || sku === 'S0';

/**
 * Represents a modal to create new azure cs account.
 *
 * @param props The properties passed to the modal.
 */
export const AzureCsAccountCreateForm = observer((props: AzureCsAccountCreateFormProps) => {
    const {
        onSubmit,
        mode,
        subscriptionId,
        showRegion = true,
        showPricingTier = true,
        onAzureCsAccountDtoChange,
        showManagedIdentityToggle = true,
        pricingFilterPredicate = defaultPredicate
    } = props;
    const { localize } = useLocalization();
    const { rootStore, dispatcher } = useGlobalState();

    const { subscriptionStoreHouse, resourceGroupStoreHouse, tenantStoreHouse, session } = rootStore.azureStore;
    const currentRegion = getOrDefaultRegionFromResource(session.csAccountStore);
    const currentTenant: AzureTenantStore = tenantStoreHouse.findById(session.tenantId) || { id: '', name: '', defaultDomain: '' };

    const { formState, formStateDto, updateFormState } = useFormState<AzureCsAccountCreateFormDto>({
        pricingTier: ['F0', FormValidators.createRequiredValidator()],
        resourceGroupName: ['', FormValidators.createRequiredValidator()],
        region: [currentRegion, FormValidators.createRequiredValidator()],
        tenant: [currentTenant.name, FormValidators.createRequiredValidator()],
        isManagedIdentityEnabled: [false, FormValidators.createRequiredValidator()],
        subscriptionId: [subscriptionId ?? '', FormValidators.createRequiredValidator()],
        name: [
            '',
            FormValidators.createRequiredValidator(),
            FormValidators.createBlacklistValidator({
                characterList: '\\/:?&=*+()%$~!><%_',
                errorMessage: azureResourceModalsMessages.invalidName
            })
        ]
    });

    const resourceGroups = resourceGroupStoreHouse.items;
    const pricing: AzureSku['code'][] = (LuisConstants.Azure.LuisAllowedSkuCodes as AzureSku['code'][]).filter(pricingFilterPredicate);
    const regionsState = resolveRegionsState({
        currentRegion,
        isAuthoringCreationMode: mode === 'LUIS.Authoring',
        insideApplication: Boolean(rootStore.currentLuisAppStore)
    });

    React.useEffect(() => onAzureCsAccountDtoChange({ ...formStateDto, isValid: formState.form.valid }), [formStateDto]);

    React.useEffect(() => {
        if (formState.fields.subscriptionId.value) {
            dispatcher.dispatch('getAzureResourceGroups', { id: formState.fields.subscriptionId.value });
        }
    }, [formState.fields.subscriptionId.value]);

    const renderRegionAndTierDropdowns = (): JSX.Element => {
        const regionsDropdown = showRegion && regionsState.kind === 'regions' && (
            <LuisDropdown
                required
                isSearchEnabled={false}
                data-automation-id="regionsDropdown"
                selectedKey={formState.fields.region.value}
                errorMessage={formState.fields.region.errorMessage}
                label={localize(azureResourceModalsMessages.regionLabel)}
                placeholder={localize(azureResourceModalsMessages.regionPlaceholder)}
                onChange={(_, opt) => updateFormState({ region: opt.key as Region })}
                options={regionsState.regions.map(region => ({ key: region, text: region }))}
            />
        );

        return (
            <Stack tokens={{ childrenGap: 20 }}>
                {regionsDropdown}

                {showPricingTier && (
                    <LuisDropdown
                        required
                        isSearchEnabled={false}
                        data-automation-id="pricingTierDropdown"
                        selectedKey={formState.fields.pricingTier.value}
                        options={pricing.map(p => ({ key: p, text: p }))}
                        errorMessage={formState.fields.pricingTier.errorMessage}
                        label={localize(azureResourceModalsMessages.pricingTierLabel)}
                        ariaLabel={localize(azureResourceModalsMessages.pricingTierLabel)}
                        placeholder={localize(azureResourceModalsMessages.pricingTierPlaceholder)}
                        onChange={(_, opt) => updateFormState({ pricingTier: opt.key as AzureSku['code'] })}
                    />
                )}
            </Stack>
        );
    };

    const managedIdentityToggle = (): JSX.Element => {
        if (!showManagedIdentityToggle) {
            return null;
        }

        return (
            <Stack tokens={{ childrenGap: 5 }}>
                <Label styles={flexLabelStyles}>
                    {localize(azureResourceModalsMessages.managedIdentityLabel)}
                    <CalloutButton content={localize(azureResourceModalsMessages.managedIdentityCalloutLabel)} />
                </Label>

                <Toggle
                    data-automation-id="miToggle"
                    checked={formState.fields.isManagedIdentityEnabled.value}
                    onChange={(_, checked: boolean) => updateFormState({ isManagedIdentityEnabled: checked })}
                />
            </Stack>
        );
    };

    return (
        <Stack tokens={{ childrenGap: 20 }}>
            <Stack tokens={{ childrenGap: 2 }}>
                <LuisDropdown
                    required
                    maxHeight={300}
                    selectedKey={currentTenant.id}
                    isLoading={tenantStoreHouse.tracker.isRunning}
                    label={localize(azureResourceModalsMessages.tenantLabel)}
                    options={tenantStoreHouse.items.map(item => ({ key: item.id, text: item.name }))}
                    onChange={(_, opt) => dispatcher.dispatch('switchAzureTenantInSession', { id: opt.key as string })}
                />
                <LuisText variant="small">{localize(azureResourceModalsMessages.tenantDescription)}</LuisText>
            </Stack>

            <LuisDropdown
                required
                maxHeight={300}
                data-automation-id="subscriptionsDropdown"
                selectedKey={formState.fields.subscriptionId.value}
                isLoading={subscriptionStoreHouse.tracker.isRunning}
                errorMessage={formState.fields.subscriptionId.errorMessage}
                label={localize(azureResourceModalsMessages.subscriptionLabel)}
                placeholder={localize(azureResourceModalsMessages.subscriptionPlaceholder)}
                onChange={(_, item) => updateFormState({ subscriptionId: item.key as string })}
                ariaLabel={`${localize(azureResourceModalsMessages.subscriptionLabel)} combobox`}
                options={subscriptionStoreHouse.items.map(item => ({ key: item.id, text: item.name }))}
            />

            <LuisDropdown
                required
                maxHeight={300}
                data-automation-id="resourceGroupDropdown"
                isLoading={resourceGroupStoreHouse.tracker.isRunning}
                selectedKey={formState.fields.resourceGroupName.value}
                disabled={formState.fields.subscriptionId.value === ''}
                errorMessage={formState.fields.resourceGroupName.errorMessage}
                label={localize(azureResourceModalsMessages.resourceGroupLabel)}
                options={resourceGroups.map(item => ({ key: item.id, text: item.name }))}
                placeholder={localize(azureResourceModalsMessages.resourceGroupPlaceholder)}
                ariaLabel={`${localize(azureResourceModalsMessages.resourceGroupLabel)} combobox`}
                onChange={(_, item) => updateFormState({ resourceGroupName: item.text as string })}
            />

            <LuisTextField
                required
                onEnterKeyUp={onSubmit}
                value={formState.fields.name.value}
                data-automation-id="resourceNameField"
                onChange={(_, name) => updateFormState({ name })}
                errorMessage={formState.fields.name.errorMessage}
                label={localize(azureResourceModalsMessages.nameLabel)}
                placeholder={localize(azureResourceModalsMessages.namePlaceholder)}
            />

            {renderRegionAndTierDropdowns()}
            {managedIdentityToggle()}
        </Stack>
    );
});
